import React, { memo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button } from '@gerdau/hefesto';
import { Tag } from '@gerdau/hefesto-mais-web';

import {
  NoImageThumbnail,
  CardContent,
  ProductInfo,
  ProductPrice,
  AdditionalInfo,
  CardContainer,
  CodeInfo,
  ButtonsContainer,
  AditionalInfoContainer,
  PriceAgreementContainer,
  InfoContainer
} from './styles';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { addToast } from '../../../store/ducks/toast/actions';
import { EToastTypes } from '../../../store/ducks/toast/types';
import { sendTagging } from '../../../store/ducks/tagging/actions';
import { useLocation } from 'react-router-dom';
import { getTaggingInformationScreen } from '../../../utils/Tagging';
import {
  CartItemPayload,
  SortProductsItem
} from '../../../services/MVP/models';
import { useCart } from '../../../pages/Purchase/LongSteel/ShoppingCartMVP/useCart';
import { useCartService } from '../../../services/MVP/useCartService';
import LoadingModal from '../../LoadingModal';
import { EOrderSettingsRoutes } from '../../../pages/Purchase/LongSteel/OrderSettingsMvp/orderSettings.routes';
import { currencyFormatter } from '../../../pages/Purchase/LongSteel/CheckoutMVP/utils/formatterHelper';
import { updateCartItemsQuantity } from '../../../store/ducks/cart-items-mvp/actions';
import { ApplicationState } from '../../../store';

export interface ProductImageProps {
  desktopSource: string;
  mobileSource?: string;
  breakpoint?: number;
}

export const ProductImage = styled.div<ProductImageProps>`
  background: url(${(props) => `${props.desktopSource}?width=128`});
  min-height: 8rem;
  max-height: 8rem;
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  @media (max-width: ${({ breakpoint }) => breakpoint ?? '992px'}) {
    background-image: url(${(props) => props.mobileSource});
  }
`;

interface ProductCardProps {
  agreedPrice?: boolean;
  image?: ProductImageProps;
  product?: SortProductsItem;
  showLink?: boolean;
  typeCode?: 'gerdauCode' | 'internalCode';
}

const AgreementChip = ({ agreedPrice }: { agreedPrice?: boolean }) => {
  if (!agreedPrice) {
    return null;
  }

  return (
    <PriceAgreementContainer>
      <Tag variant="success">Preço acordado</Tag>
    </PriceAgreementContainer>
  );
};

const WithoutPriceChip = ({ withoutPrice }: { withoutPrice?: boolean }) => {
  if (!withoutPrice) {
    return null;
  }

  return (
    <PriceAgreementContainer>
      <Tag variant="danger">Produto indisponível</Tag>
    </PriceAgreementContainer>
  );
};

const ImageProduct = ({ image }: { image?: ProductImageProps }) => {
  if (!image) {
    return (
      <NoImageThumbnail>Imagem indisponível para este produto</NoImageThumbnail>
    );
  }

  return <ProductImage {...image} />;
};

const ProductDetails = ({
  showLink,
  onClick
}: {
  showLink: boolean;
  onClick: () => void;
}) => {
  if (!showLink) {
    return null;
  }

  return (
    <Button
      name="btn-show-details"
      variant="tertiary"
      onClick={onClick}
      children={'Mostrar detalhes'}
    />
  );
};

const ProductCard = ({
  agreedPrice,
  product,
  image,
  showLink = true,
  typeCode
}: ProductCardProps) => {
  if (!product) {
    return null;
  }

  const shoppingCartState = useSelector(
    (state: ApplicationState) => state.longSteelShoppingCart
  );

  const cartQuantity = useSelector(
    (state: ApplicationState) => state.cartItemsMvp.quantity
  );

  const { addItemToCart } = useCart();
  const { createNewCart } = useCartService();
  const { getCartId } = useCartService();
  const { push } = useHistory();

  const [loading, setLoading] = React.useState(false);

  const dispatch = useDispatch();
  const descricao_gpm =
    product.custom_attributes.find(
      (att) => att.attribute_code === 'descricao_gpm'
    )?.value ?? '';

  const { pathname } = useLocation();
  const { commerceScreenName } = getTaggingInformationScreen(pathname);

  // Buscar aqui key do carrinho ativo
  const handleAddProduct = async () => {
    if (!product.price || Number(product.price.creditPrice) <= 0) {
      return null;
    }
    try {
      setLoading(true);
      const existingCart = getCartId(shoppingCartState.emitterId);
      let cart;
      let currentQuantity;

      if (!existingCart || existingCart.length === 0) {
        const newCart = await createNewCart(
          shoppingCartState.emitterId,
          shoppingCartState.receiverId,
          true
        );
        cart = newCart?.cartId;
        currentQuantity = 1;
      } else {
        cart = existingCart;
        currentQuantity = cartQuantity + 1;
      }

      if (!cart || cart.length === 0) {
        throw new Error();
      }

      if (currentQuantity > 30) {
        return dispatch(
          addToast({
            type: EToastTypes.DANGER,
            message: 'Seu carrinho atingiu o limite de 30 produtos.',
            durationInMS: 3000,
            labelActionOne: 'X'
          })
        );
      }

      const formattedProduct = {
        sku: product.sku,
        qty: 1,
        quote_id: cart,
        extension_attributes: {
          measure_unity_gerdau: product.price.um,
          destination_gerdau: product.price.destination,
          payment_type_gerdau: product.price.paymentTerms,
          has_agreed_price_gerdau: product.price.isAgreementPrice.length !== 0,
          ...(product.price.salesAgreementData.expirationDate && {
            price_agreement_date_gerdau:
              product.price.salesAgreementData.expirationDate
          }),
          sap_fields: {
            o_header_sap: [],
            o_item_sap: [],
            o_price_sap: []
          }
        }
      } as CartItemPayload;

      const addItem = await addItemToCart(formattedProduct, currentQuantity, {
        withOptions: false,
        withPrice: false
      });

      if (addItem.item_id) {
        dispatch(
          sendTagging({
            event: 'add_to_cart',
            ecommerce: {
              currency: 'BRL',
              value: `${product.price.creditPrice}`,
              screenName: commerceScreenName,
              tipo_compra: 'vitrine',
              paginacao: '1',
              items: [
                {
                  screenName: commerceScreenName,
                  price: `${product.price.creditPrice}`,
                  unidade_de_medida: `${product.price.um}`,
                  item_id: `${product.sku}`,
                  item_name: `${product.name}`,
                  tipo_compra: `vitrine`,
                  item_category: `${product.type_id}`,
                  cod_gerdau: `${product.sku}`,
                  preco_acordado: product.price.isAgreementPrice.length > 0,
                  quantity: product.price.quantity ?? '1'
                }
              ]
            }
          })
        );

        dispatch(updateCartItemsQuantity(currentQuantity ?? 0));

        dispatch(
          addToast({
            message: `Produto adicionado ao carrinho. (SKU ${product.sku})`,
            type: EToastTypes.SUCCESS,
            durationInMS: 5000,
            spaceToTop: 77
          })
        );
        return;
      }

      throw new Error();
    } catch (error) {
      console.error(error);
      dispatch(
        addToast({
          message: 'Falha ao inserir novo item',
          type: EToastTypes.DANGER,
          durationInMS: 5000,
          spaceToTop: 77
        })
      );
    } finally {
      setLoading(false);
    }
  };

  // Navegação para a destalhes do produto
  const handleShowDetails = () => {
    dispatch(
      sendTagging({
        event: 'select_item',
        ecommerce: {
          currency: 'BRL',
          value: `${product.price}`,
          screenName: 'vitrine',
          tipo_compra: 'vitrine',
          paginacao: '1',
          items: [
            {
              screenName: 'vitrine',
              price: `${product.price.creditPrice}`,
              unidade_de_medida: `${product.price.um}`,
              item_id: `${product.sku}`,
              item_name: `${product.name}`,
              tipo_compra: `vitrine`,
              item_category: `${descricao_gpm}`,
              cod_gerdau: `${product.sku}`,
              preco_acordado: product.price.isAgreementPrice.length > 0,
              quantity: product.price.quantity ?? '1'
            }
          ]
        }
      })
    );
    push(
      `${EOrderSettingsRoutes.CATALOG}/${EOrderSettingsRoutes.PRODUCT}/${product.sku}`
    );
  };

  const hasFilterInternalCode =
    product.price?.customerProduct && typeCode === 'internalCode';
  const productCode = hasFilterInternalCode
    ? product.price.customerProduct
    : product.sku;

  const productWithoutPrice =
    product.price?.creditPrice === '' ||
    !product.price?.creditPrice ||
    Number(product.price?.creditPrice) <= 0;

  return (
    <CardContainer className="product-cards">
      <LoadingModal
        enabled={loading}
        title="Aguarde alguns instantes"
        message="Carregando os dados..."
      />
      <AgreementChip agreedPrice={agreedPrice} />
      <WithoutPriceChip withoutPrice={productWithoutPrice} />
      <ImageProduct image={image} />

      <CardContent>
        <CodeInfo>
          Cód. {hasFilterInternalCode ? 'Interno' : 'Gerdau'}: {productCode}
        </CodeInfo>
        <ProductInfo $title>{descricao_gpm}</ProductInfo>
        <ProductInfo>{product.name}</ProductInfo>
        <InfoContainer>
          {!productWithoutPrice && (
            <>
              <ProductPrice>
                {currencyFormatter({
                  amount: agreedPrice
                    ? Number(product.price.salesAgreementData.salesAgreement)
                    : Number(product.price.creditPrice),
                  currency: 'BRL'
                })}
                <span style={{ fontSize: '0.88rem', fontWeight: '400' }}>
                  /{product.price.um}
                </span>
              </ProductPrice>
              <AditionalInfoContainer>
                <AdditionalInfo
                  title={`Preço/${product.price.um}: ${currencyFormatter({
                    amount: agreedPrice
                      ? Number(product.price.salesAgreementData.salesAgreement)
                      : Number(product.price.creditPrice),
                    currency: 'BRL'
                  })}`}
                >
                  Preço/
                  {agreedPrice
                    ? product.price.salesAgreementData.agreementUm
                    : product.price.um}
                  :{' '}
                  {currencyFormatter({
                    amount: agreedPrice
                      ? Number(product.price.salesAgreementData.salesAgreement)
                      : Number(product.price.creditPrice),
                    currency: 'BRL'
                  })}
                </AdditionalInfo>
                <AdditionalInfo
                  title={`Pagamento: ${
                    agreedPrice
                      ? product.price.salesAgreementData.paymentTermsDescription
                      : product.price.paymentTermsDescription
                  }`}
                >
                  Pagamento:{' '}
                  {agreedPrice
                    ? product.price.salesAgreementData.paymentTermsDescription
                    : product.price.paymentTermsDescription}
                </AdditionalInfo>
                <AdditionalInfo
                  title={`Destinação: ${
                    agreedPrice
                      ? product.price.salesAgreementData.destination
                      : product.price.destination
                  }`}
                >
                  Destinação:{' '}
                  {agreedPrice
                    ? product.price.salesAgreementData.destinationDescription
                    : product.price.destinationDescription}
                </AdditionalInfo>
              </AditionalInfoContainer>
            </>
          )}
          <ButtonsContainer>
            <Button
              onClick={handleAddProduct}
              name="button-card"
              variant="secondary"
              disabled={productWithoutPrice}
            >
              {productWithoutPrice
                ? 'Produto indisponível'
                : 'Adicionar ao carrinho'}
            </Button>
            <ProductDetails onClick={handleShowDetails} showLink={showLink} />
          </ButtonsContainer>
        </InfoContainer>
      </CardContent>
    </CardContainer>
  );
};

export default memo(ProductCard);
